import { type SortEnd } from "@common/declarations";
import { SortableContainerHOC, type SortableContainerProps, SortableElementHOC } from "@common/hocs/dnd";
import classnames from "classnames";
import { isNull, isUndefined } from "lodash";
import * as React from "react";
import Accordion, { AccordionMode, type AccordionProps, AutoExpandItems } from "./component";
import AccordionItem, { type AccordionItemPropsInner } from "./Item";

const SortableAccordionInner = SortableContainerHOC<AccordionProps>(Accordion);

const SortableAccordion = ({
  autoExpand = AutoExpandItems.First,
  mode = AccordionMode.Accordion,
  ...props
}: AccordionProps &
  Omit<SortableContainerProps, "useDragHandle" | "useWindowAsScrollContainer" | "helperClass"> & {
    useWindowAsScrollContainer: boolean;
  }) => {
  const [activeItemIndex, setActiveItemIndex] = React.useState(props.activeItemIndex);
  const [oldActiveItemIndex, setOldActiveItemIndex] = React.useState(props.activeItemIndex);
  const [isSorting, setIsSorting] = React.useState(false);

  const onSortStart = React.useCallback(() => {
    setOldActiveItemIndex(activeItemIndex);
    setIsSorting(true);
    props.onSortStart && props.onSortStart();
  }, [props.onSortStart, setOldActiveItemIndex, activeItemIndex, setIsSorting]);

  const onSortEnd = React.useCallback(
    (sort: SortEnd) => {
      if (sort.oldIndex !== sort.newIndex) {
        props.onSortEnd && props.onSortEnd(sort);
        if (!isUndefined(oldActiveItemIndex) && !isNull(oldActiveItemIndex)) {
          const min = Math.min(sort.oldIndex, sort.newIndex);
          const max = Math.max(sort.oldIndex, sort.newIndex);

          if (min <= oldActiveItemIndex && oldActiveItemIndex <= max) {
            if (sort.oldIndex === oldActiveItemIndex) {
              setActiveItemIndex(sort.newIndex);
            } else if (sort.newIndex > oldActiveItemIndex || sort.oldIndex === oldActiveItemIndex - 1) {
              setActiveItemIndex(oldActiveItemIndex - 1);
            } else {
              setActiveItemIndex(oldActiveItemIndex + 1);
            }
          } else {
            setActiveItemIndex(oldActiveItemIndex);
          }
        }
      }

      setIsSorting(false);
    },
    [props.onSortEnd, oldActiveItemIndex, setActiveItemIndex, setIsSorting]
  );

  const onItemActivated = React.useCallback(
    (index: number | null) => {
      props.onItemActivated && props.onItemActivated(index);
      setActiveItemIndex(index);
    },
    [setActiveItemIndex, props.activeItemIndex]
  );

  return (
    <SortableAccordionInner
      {...props}
      activeItemIndex={activeItemIndex}
      autoExpand={autoExpand}
      className={classnames(props.className, { sorting: isSorting })}
      mode={mode}
      noAnimation={isSorting}
      onItemActivated={onItemActivated}
      onSortStart={onSortStart}
      onSortEnd={onSortEnd}
      useHandles
    />
  );
};
SortableAccordion.Item = SortableElementHOC<AccordionItemPropsInner>(AccordionItem);
export default SortableAccordion;
